import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, X } from 'lucide-react';
import type { Serie } from '../services/seriesService';

interface ConfirmDeleteSerieModalProps {
  isOpen: boolean;
  serie: Serie | null;
  onClose: () => void;
  onConfirm: (serie: Serie) => Promise<void>;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.12);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #1e293b;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #dc2626;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  padding: 16px 20px 20px;
`;

const Text = styled.p`
  margin: 0 0 12px 0;
  color: #475569;
`;

const HighlightBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 20px 20px;
`;

const Button = styled.button<{ $variant: 'secondary' | 'danger' }>`
  border-radius: 8px;
  border: 1px solid;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${props => props.$variant === 'danger' ? `
    background: #dc2626;
    border-color: #dc2626;
    color: white;
  ` : `
    background: white;
    border-color: #d1d5db;
    color: #334155;
  `}
`;

export const ConfirmDeleteSerieModal: React.FC<ConfirmDeleteSerieModalProps> = ({
  isOpen,
  serie,
  onClose,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!serie) return;

    setIsLoading(true);
    try {
      await onConfirm(serie);
      onClose();
    } catch (error) {
      console.error('Erro ao excluir série:', error);
    } finally {
      setIsLoading(false);
    }
  };
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <Header>
          <HeaderLeft>
            <AlertTriangle size={22} />
            <Title>Confirmar Exclusão</Title>
          </HeaderLeft>
          <CloseButton onClick={onClose} disabled={isLoading} aria-label="Fechar">
            <X size={20} />
          </CloseButton>
        </Header>

        <Body>
          <Text>
            Tem certeza que deseja remover a série <strong>{serie?.nome}</strong>?
          </Text>

          <HighlightBox>
            <Text style={{ marginBottom: 6 }}>
              Tipo: {serie?.tipoEnsino === 'curricular' ? 'Curricular' : 'Extracurricular'}
            </Text>
            <Text style={{ marginBottom: 0 }}>
              Valor: R$ {serie?.valorMensalidade?.toFixed(2).replace('.', ',')}
            </Text>
          </HighlightBox>

          <Text style={{ marginBottom: 0 }}>
            Esta ação não pode ser desfeita.
          </Text>
        </Body>

        <Footer>
          <Button $variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button $variant="danger" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Removendo...' : 'Confirmar Exclusão'}
          </Button>
        </Footer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ConfirmDeleteSerieModal;
